import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getMeetingAssistantConfig } from '@/api'
import AppMessage from '@/components/message/index.js'
import { useAppStore } from '@/store/useAppStore'
import {
  cancelSession,
  createSession,
  getSession,
  getSessionFile,
  getFileJson,
  getSessionEventList
} from '@/api/new-agent/qca.js'
import utils from '@/utils'
import emitter from '@/utils/mitt'
import { QODER_BLOCK_TYPES, useQoderCloudAgent } from './use-qoder-cloud-agent'
import { QODER_HISTORY_EVENT_LIMIT, formatSessionHistoryMessages } from './use-qoder-message-format'

// 两端共享会话编排；布局、输入框和侧边栏由页面负责。
// eslint-disable-next-line max-lines-per-function
export function useQoderConversation({
  scroll,
  onPanelVisibilityChange,
  onRefreshSessions,
  onOpenArtifact,
  onResetInput
} = {}) {
  const route = useRoute()
  const router = useRouter()
  const appStore = useAppStore()

  // 隔离切会话或卸载后的历史、创建与产物请求。
  let conversationVersion = 0
  let artifactRequestVersion = 0
  const submitting = ref(false)
  const stopLoading = ref(false)

  // 会话状态
  const sessionId = ref('')
  const messageList = ref([])
  const hasUnfinishedSession = ref(false) // 控制输入禁用，并决定是否继续消费 SSE 消息
  const hasMore = ref(false) // 当前会话是否还有更早的历史事件可翻页
  const historyLoading = ref(false) // 是否正在加载更早的历史事件
  const uploadSize = ref(10) // 文件大小默认10MB
  const uploadExtensions = [
    'pdf',
    'doc',
    'docx',
    'md',
    'txt',
    'ppt',
    'pptx',
    'csv',
    'xls',
    'xlsx',
    'png',
    'jpg'
  ]
  const aiInputRef = ref(null)

  // 消息分屏状态
  const panelVisible = ref(false)
  const panelData = ref({})

  const sessionLoading = ref(false)

  const { scrollToBottom, preserveScrollPosition, resetMessageScroll } = scroll

  // Qoder Cloud Agent 会话能力
  const {
    sendQoderSessionMessage,
    finishQoderMessage,
    startNewConversation,
    getQoderSseEventStream
  } = useQoderCloudAgent({
    sessionId,
    messageList,
    hasUnfinishedSession,
    scrollToBottom,
    onRefreshSessions
  })

  /**
   * 本轮输出未结束时，列表末尾展示的等待文案。
   * 取最后一条消息判定类型：AI 消息总是追加在用户消息之后。
   */
  const waitingText = computed(() => {
    if (!hasUnfinishedSession.value) return ''

    const lastMessage = messageList.value[messageList.value.length - 1]
    if (!lastMessage || lastMessage.type !== 'qoder') return '等待模型响应'

    const blockType = lastMessage.blockType
    if (blockType === QODER_BLOCK_TYPES.MESSAGE) return '正在生成回复中'
    if (blockType === QODER_BLOCK_TYPES.ARTIFACT) return '正在生成总结'
    // thinking / tool_use 块内已自带流光文字，末尾不再重复展示。
    if (blockType === QODER_BLOCK_TYPES.TOOL_USE) {
      if (lastMessage.toolName === 'Bash') {
        return '正在执行命令'
      } else if (lastMessage.toolName === 'Read') {
        return '正在读取文件'
      } else if (lastMessage.toolName === 'Write') {
        return '正在写入文件'
      } else if (lastMessage.toolName === 'Edit') {
        return '正在编辑文件'
      } else if (lastMessage.toolName === 'Glob') {
        return '正在进行代码文件查找'
      } else if (lastMessage.toolName === 'Grep') {
        return '正在进行文件内容搜索'
      } else if (lastMessage.toolName === 'WebFetch') {
        return '正在进行页面读取'
      } else if (lastMessage.toolName === 'WebSearch') {
        return '正在联网搜索'
      } else if (lastMessage.toolName === 'DeliverArtifacts') {
        return '正在获取交付产物'
      }
    }
    return '等待模型响应'
  })
  /**
   * 复制消息内容，并反馈复制结果。
   */
  const handleCopy = (content) => {
    utils
      .copyText(content)
      .then(() => {
        AppMessage.success('复制成功')
      })
      .catch(() => {
        AppMessage.error('复制失败，请手动复制')
      })
  }

  const closePanel = () => {
    artifactRequestVersion += 1
    onPanelVisibilityChange?.(false)
    panelVisible.value = false
  }

  /**
   * 重置历史消息分页状态。
   */
  const resetSessionPagination = () => {
    hasMore.value = false
    historyLoading.value = false
  }

  /**
   * 重置当前会话关联的消息、分页、滚动和分屏状态。
   */
  const resetConversationState = () => {
    conversationVersion += 1
    onResetInput?.()
    sessionLoading.value = false
    submitting.value = false
    stopLoading.value = false
    // 递增 SSE 请求版本，使旧请求的后续事件不再更新当前页面。
    startNewConversation()
    messageList.value = []
    hasUnfinishedSession.value = false
    resetMessageScroll()
    resetSessionPagination()
    closePanel()
  }

  /**
   * 新建空白会话。
   */
  const handleNewConversation = () => {
    resetConversationState()
    finishQoderMessage()
    sessionId.value = ''
    appStore.setAiSalesMentorSession(null)
  }

  /**
   * 打开指定历史会话。
   * @param session 会话信息
   */
  const handleOpenSession = async (_sessionId) => {
    // 清空 Store 中已消费的待打开会话，避免页面再次挂载时重复打开。
    appStore.setOpenAiSessionId('')
    resetConversationState()
    sessionId.value = _sessionId
    const version = conversationVersion
    try {
      sessionLoading.value = true
      const [res, sessionRes] = await Promise.all([
        getSessionEventList({ id: _sessionId, limit: QODER_HISTORY_EVENT_LIMIT }),
        getSession({ id: _sessionId })
      ])

      // 请求期间又切到别的会话时，丢弃这次的历史数据。
      if (version !== conversationVersion) return
      sessionLoading.value = false
      appStore.setAiSalesMentorSession({
        ...appStore.aiSalesMentorSession,
        ...sessionRes.result,
        sessionId: _sessionId
      })
      // 历史接口按时间倒序返回，聊天区按正序展示：最早一条在最上面，最新一条在最下面。
      messageList.value = formatSessionHistoryMessages(res).reverse()
      markHistoryCompletionTimes(
        messageList.value,
        !['running', 'rescheduling'].includes(sessionRes?.result?.status)
      )
      // hasMore 为真说明滚动到顶部时还可以加载更早的事件。
      hasMore.value = Boolean(res?.result?.hasMore)
      await nextTick()
      if (version !== conversationVersion) return
      scrollToBottom(true)
      if (['running', 'rescheduling'].includes(sessionRes?.result?.status)) {
        hasUnfinishedSession.value = true
        // SSE 是长连接，不能 await：否则本函数要等流结束才返回，
        // 末尾的会话列表刷新会被一并推迟到 SSE 结束之后。
        // 旧流的失效交给 requestId + AbortController 兜底，这里无需等待结果。
        getQoderSseEventStream({
          sessionId: sessionId.value,
          lastEventId: res?.result?.firstId
        })
      }
    } catch (error) {
      if (version !== conversationVersion) return
      sessionLoading.value = false
      console.error('Get qoder session event list failed:', error)
      if (sessionId.value === _sessionId) AppMessage.error('历史会话加载失败')
    }
    setTimeout(() => {
      // 等待 SSE 连接建立，确保会话列表更新。
      onRefreshSessions?.()
    }, 1000)
  }

  /**
   * 加载更早的历史事件，并拼接到消息列表最前面。
   * 列表按时间正序排列，因此以当前最早一条消息的 id 作为 before 向前翻页。
   */
  const handleViewHistory = async () => {
    if (sessionLoading.value || historyLoading.value || !hasMore.value) return

    const afterId = messageList.value[0]?.id
    if (!afterId) return

    const _sessionId = sessionId.value
    const version = conversationVersion
    historyLoading.value = true
    try {
      const res = await getSessionEventList({
        id: _sessionId,
        limit: QODER_HISTORY_EVENT_LIMIT,
        after: afterId
      })
      // 请求期间切到别的会话时，丢弃这次的历史数据。
      if (version !== conversationVersion) return

      const historyMessages = formatSessionHistoryMessages(res).reverse()
      // 在顶部插入内容会顶开视口，这里保持原有消息的视觉位置不跳动。
      await preserveScrollPosition(() => {
        messageList.value = [...historyMessages, ...messageList.value]
        markHistoryCompletionTimes(messageList.value)
      })
      if (version === conversationVersion) hasMore.value = Boolean(res?.result?.hasMore)
    } catch (error) {
      console.error('Get qoder session history events failed:', error)
      if (version === conversationVersion) AppMessage.error('历史对话加载失败')
    } finally {
      if (version === conversationVersion) historyLoading.value = false
    }
  }

  /**
   * 提交用户消息并启动新的 AI 执行。
   */
  const handleSubmit = async (payload) => {
    if (hasUnfinishedSession.value || submitting.value || sessionLoading.value || stopLoading.value)
      return
    if (!payload?.text?.trim()) return
    const version = conversationVersion
    submitting.value = true
    try {
      if (!sessionId.value) {
        const res = await createSession()
        if (version !== conversationVersion) return
        sessionId.value = res.result?.id
        appStore.setAiSalesMentorSession({ ...res.result, sessionId: sessionId.value })
        setTimeout(() => {
          // 等待 SSE 连接建立，确保会话列表更新。
          onRefreshSessions?.()
        }, 1000)
      }
    } catch (error) {
      if (version !== conversationVersion) return
      if (error?.code === 100009) {
        return
      }
      AppMessage.error('创建会话失败')
      return
    } finally {
      if (version === conversationVersion) submitting.value = false
    }
    // 先展示用户消息，避免等待 SSE 建连后页面才响应。
    messageList.value.push({
      id: new Date().getTime(),
      type: 'user',
      processedAt: new Date().toISOString(),
      message: {
        referenceFileUrlList: payload.files,
        content: payload.text
      }
    })
    scrollToBottom(true)
    hasUnfinishedSession.value = true

    const params = {
      payload: {
        text: payload.text
      },
      referenceFileUrlList: payload.files
    }
    if (payload.tipShow) {
      params.businessData = {
        formId: payload.formId,
        businessType: payload.businessType,
        dataIdList: payload.selectIds
      }
    }
    sendQoderSessionMessage(params) // 发送消息到 Qoder 会话
  }

  /**
   * 渲染用列表：把连续的 tool_use 合并成一个分组节点，其余消息原样透传。
   * 分组形态为 { renderKey, isToolGroup: true, items: [...] }，整组由 tool-use-group 渲染并统一做折叠动画。
   */
  const renderList = computed(() => {
    const list = []
    messageList.value.forEach((item) => {
      if (item.blockType !== QODER_BLOCK_TYPES.TOOL_USE) {
        list.push(item)
        return
      }

      const prevGroup = list[list.length - 1]
      if (prevGroup?.isToolGroup) {
        prevGroup.items.push(item)
        return
      }
      // key 与组首绑定：流式追加成员时复用同一个分组节点，避免整组重建丢掉折叠态。
      list.push({
        renderKey: `tool-group-${item.id}`,
        isToolGroup: true,
        head: item,
        items: [item]
      })
    })

    return list
  })

  const getCompletedAt = (item) =>
    item.isToolGroup ? item.items[item.items.length - 1]?.completedAt : item.completedAt

  const markHistoryCompletionTimes = (messages, sessionFinished = false) => {
    messages.forEach((item, index) => {
      const previous = messages[index - 1]
      if (item.type === 'user' && previous?.type === 'qoder') {
        previous.completedAt = previous.eventProcessedAt
      }
    })
    const lastMessage = messages[messages.length - 1]
    if (sessionFinished && lastMessage?.type === 'qoder') {
      lastMessage.completedAt = lastMessage.eventProcessedAt
    }
  }

  /**
   * 取最后一条非思考占位的消息：思考只是等待提示，不参与"是否已输出完"的判断。
   * @returns {Object|null} 最后一条实质消息
   */
  const findLastNonThinkingMessage = () => {
    for (let index = messageList.value.length - 1; index >= 0; index -= 1) {
      const item = messageList.value[index]
      if (item.blockType !== QODER_BLOCK_TYPES.THINKING) return item
    }
    return null
  }

  /**
   * tool_use 组是否仍在输出：本轮未结束，且该组正好位于消息列表末尾。
   * 一旦下方追加了任何非 tool_use 消息（思考、正文、产物等），组尾不再是列表尾，随即自动收起。
   * @param {Object} group 分组节点
   * @returns {boolean} 是否需要保持展开
   */
  const isToolGroupActive = (group) => {
    if (!hasUnfinishedSession.value) return false

    // 思考占位会被插入正在输出的工具组之后，判断"是否仍在输出"时要跳过它，
    // 否则工具组会在思考出现的一瞬间被误判为已结束而先收起、下一工具到来再展开。
    const lastMessage = findLastNonThinkingMessage()
    const lastGroupItem = group.items[group.items.length - 1]
    return Boolean(lastMessage) && Boolean(lastGroupItem) && lastMessage.id === lastGroupItem.id
  }

  /**
   * 同步各组的收起态：用户手动点过的组保持用户的选择，其余跟随输出状态自动收展。
   * 历史会话加载完成后没有进行中的组，因此默认整列表都是收起的。
   */
  watch(
    renderList,
    (list) => {
      list.forEach((group) => {
        if (!group.isToolGroup || group.head.groupToggled) return
        group.head.groupCollapsed = !isToolGroupActive(group)
      })
    },
    { immediate: true }
  )

  /**
   * 点击 tool_use 组首：翻转整组收起态，并打上手动标记，不再被自动收展覆盖。
   * 具体展开收起动画由 tool-use-group 统一执行。
   * @param {Object} message 组首 tool_use 消息
   */
  const handleToolUseToggle = (message) => {
    message.groupToggled = true
    message.groupCollapsed = !message.groupCollapsed
  }

  // 文件投递物下载中：避免重复点击重复拉链接。
  const artifactDownloading = ref(false)

  /**
   * 文件投递物卡片点击下载：移动端复制下载链接，桌面端交给子组件回调触发下载。
   * 拿不到 fileId 的文件（历史回退路径）无法下载，这里直接忽略。
   * @param {Object} block 投递物消息
   * @param {Function} download 子组件传入的下载回调，入参为文件下载链接
   */
  const handleArtifactDownload = async (block, download) => {
    if (!block?.fileId || typeof download !== 'function' || artifactDownloading.value) return

    artifactDownloading.value = true
    try {
      const res = await getSessionFile({ fileId: block.fileId, sessionId: sessionId.value })
      const fileUrl = res?.result?.fileUrl
      if (!fileUrl) {
        AppMessage.warning('文件链接获取失败，暂时无法下载')
        return
      }
      if (utils.isMobile()) {
        await new Promise((resolve) => {
          utils.copyToClipboard(
            fileUrl,
            () => {
              AppMessage.success('下载链接已复制')
              resolve()
            },
            () => {
              AppMessage.error('复制下载链接失败，请重试')
              resolve()
            }
          )
        })
        return
      }
      const downloaded = await download(fileUrl)
      if (downloaded) AppMessage.success('下载成功')
    } catch (error) {
      console.error('[artifact] download failed', error)
      AppMessage.warning('文件下载失败，请稍后重试')
    } finally {
      artifactDownloading.value = false
    }
  }

  const handleArtifactClick = async (block) => {
    if (!block?.fileId) return
    if (onOpenArtifact) return onOpenArtifact(block)
    const version = conversationVersion
    const request = ++artifactRequestVersion
    try {
      const res = await (block.isJson
        ? getFileJson({ fileId: block.fileId })
        : getSessionFile({ fileId: block.fileId, sessionId: sessionId.value }))
      if (version !== conversationVersion || request !== artifactRequestVersion) return
      panelData.value = block.isJson
        ? { ...res.result, fileId: block.fileId, type: 'practice' }
        : res.result
      panelVisible.value = true
      onPanelVisibilityChange?.(true)
    } catch (error) {
      if (version === conversationVersion) AppMessage.error('产物加载失败，请稍后重试')
    }
  }

  /**
   * 终止当前正在执行的会话。
   */
  const handleStop = async () => {
    if (stopLoading.value) return
    const currentSessionId = sessionId.value
    const version = conversationVersion
    if (!currentSessionId) {
      AppMessage.success('已终止会话')
      return
    }
    stopLoading.value = true
    try {
      await cancelSession({ id: currentSessionId })
      await new Promise((resolve) => setTimeout(resolve, 1000))
      if (version !== conversationVersion) return
      startNewConversation()
      AppMessage.success('已终止会话')
    } catch (error) {
      if (version !== conversationVersion) return
      console.error('Cancel qoder session failed:', error)
      AppMessage.error('终止会话失败，请重试')
    } finally {
      if (version === conversationVersion) stopLoading.value = false
    }
  }

  /**
   * 获取当前场景的文件上传大小配置。
   */
  const getUploadConfig = () => {
    getMeetingAssistantConfig({
      type: 12
    })
      .then((res) => {
        if (res.result?.data?.config) {
          // 后端以 JSON 字符串返回上传配置。
          const data = JSON.parse(res.result.data.config)
          const fileSize = Number(data.fileSize)
          if (fileSize) {
            uploadSize.value = fileSize
          }
        }
      })
      .catch((error) => console.error('Get upload config failed:', error))
  }

  const checkAiAgentQuickOpen = async () => {
    const query = route.query
    if (query.useAiAgentQuickOpen === '1' && appStore.aiAgentQuickOpen !== null) {
      console.log('aiAgentQuickOpen:', appStore.aiAgentQuickOpen)
      aiInputRef.value?.onSetValue({
        businessType: appStore.aiAgentQuickOpen.businessType,
        dataIdList: JSON.parse(appStore.aiAgentQuickOpen.dataIdList),
        value: appStore.aiAgentQuickOpen.analystContent,
        formId: appStore.aiAgentQuickOpen.formId
      })
      appStore.setAiAgentQuickOpen(null)
    } else if (query.useAiAgentQuickOpen === '2') {
      await aiInputRef.value?.onSetValue({
        businessType: query.businessType,
        dataIdList: JSON.parse(query.dataIdList),
        value: query.analystContent,
        formId: query.formId
      })
      await router.replace({ path: route.path, query: {}, hash: route.hash })
    }
  }

  onMounted(async () => {
    getUploadConfig()
    // 侧边菜单通过全局事件创建新会话或打开历史会话。
    emitter.on('newAiSalesMentorConversation', handleNewConversation)
    emitter.on('openSession', handleOpenSession)
    const initialSessionId = appStore.aiOpenSessionId
    if (initialSessionId) handleOpenSession(String(initialSessionId))
    await nextTick()
    if (!utils.isMobile()) checkAiAgentQuickOpen()
  })

  onBeforeUnmount(() => {
    // 失效当前 SSE 和 HTTP 请求，并移除全局事件监听。
    conversationVersion += 1
    startNewConversation()
    appStore.setOpenAiSessionId('')
    appStore.setAiSalesMentorSession(null)
    emitter.off('newAiSalesMentorConversation', handleNewConversation)
    emitter.off('openSession', handleOpenSession)
  })

  return {
    sessionId,
    messageList,
    renderList,
    hasUnfinishedSession,
    submitting,
    stopLoading,
    sessionLoading,
    historyLoading,
    hasMore,
    waitingText,
    uploadSize,
    uploadExtensions,
    aiInputRef,
    panelVisible,
    panelData,
    handleSubmit,
    handleStop,
    handleNewConversation,
    handleOpenSession,
    handleViewHistory,
    handleCopy,
    handleToolUseToggle,
    getCompletedAt,
    handleArtifactClick,
    handleArtifactDownload,
    closePanel
  }
}
